import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ApiConfiguration } from '../../core/api/api-configuration';
import { AlertService } from '../../shared/services/alert.service';
import { Editor, editorConfig } from '../../shared/ckeditor/ckeditor-config';

interface PatientNotesModel {
  ID?: number;
  Notes?: string;
}

@Component({
  selector: 'app-notes',
  templateUrl: './notes.component.html',
  styleUrl: './notes.component.css',
  standalone: false,
})
export class NotesComponent implements OnInit {
  notes = signal('');
  selectedPatientID: number = 0;
  isLoading = signal(false);
  isSaving = signal(false);
  initialNotes: string = '';

  public Editor = Editor;
  public editorConfig = editorConfig;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private apiConfig: ApiConfiguration,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.route.parent?.params.subscribe((params) => {
      this.selectedPatientID = +params['patientID'];
      this.loadPatientNotes();
    });
  }

  private loadPatientNotes(): void {
    if (!this.selectedPatientID) {
      return;
    }

    this.isLoading.set(true);
    const url = `${this.apiConfig.rootUrl}/api/Supervisor/GetPatientNotes`;
    this.http
      .get<PatientNotesModel>(url, {
        params: {
          patientID: this.selectedPatientID,
        },
      })
      .subscribe({
        next: (results) => {
          this.initialNotes = results?.Notes || '';
          this.notes.set(results?.Notes || '');
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Error loading patient notes', error);
          this.alertService.error('Failed to load patient notes');
          this.isLoading.set(false);
        },
      });
  }

  onNotesChange(event: any): void {
    if (event.editor) {
      const data = event.editor.getData();
      this.notes.set(data);
    }
  }

  saveNotes(): void {
    if (!this.selectedPatientID) {
      return;
    }

    this.isSaving.set(true);
    const url = `${this.apiConfig.rootUrl}/api/Supervisor/SavePatientNotes`;
    this.http
      .post<void>(
        url,
        {
          ID: this.selectedPatientID,
          Notes: this.notes(),
        },
        {
          params: {
            patientID: this.selectedPatientID,
          },
        }
      )
      .subscribe({
        next: () => {
          this.alertService.success('Patient notes saved successfully');
          this.isSaving.set(false);
        },
        error: (error) => {
          console.error('Error saving patient notes', error);
          const errorMessage =
            error?.error?.message ||
            error?.error ||
            'Failed to save patient notes';
          this.alertService.error(errorMessage);
          this.isSaving.set(false);
        },
      });
  }
}
